import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { VossProductTypeText } from '../../Constants/VOSS/vossProductType';
import { ButtonSize } from '../Fundamentals/Button';
import SpirisIcon from '../Fundamentals/Icon/SpirisIcon';
import SkynetIconButton from '../Fundamentals/IconButton/SkynetIconButton';
import type { CalculatedProduct, WorksheetInputEditor } from './PriceSimulationWorksheet.types';
import WithTooltip from '../Fundamentals/Tooltip/WithTooltip';
import WorksheetPlanRow from './WorksheetPlanRow';

interface WorksheetProductRowProps {
    product: CalculatedProduct;
    tierCount: number;
    packageCount: number;
    onFilterBySku: (sku: string) => void;
    onAdjustAllPlans: (event: MouseEvent<HTMLElement>) => void;
    editor: WorksheetInputEditor;
}

const WorksheetProductRow = ({ product, tierCount, packageCount, onFilterBySku, onAdjustAllPlans, editor }: WorksheetProductRowProps) => {
    const { t } = useTranslation();
    const linkDescription = `${t('priceSimulation.productExistsInTiers', { count: tierCount })} ${t('priceSimulation.acrossPackages', { count: packageCount })}. ${t('priceSimulation.clickToShowThem')}`;

    return (
        <>
            <tr className="worksheet-product-row">
                <td>
                    <div className="d-flex align-items-center gap-8 pl-16">
                        <SpirisIcon name="tag" size="sm" />
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center">
                                {product.name}
                                {tierCount > 1 && (
                                    <WithTooltip content={linkDescription} className="d-inline-flex ml-8">
                                        <SkynetIconButton icon="link" size={ButtonSize.SMALL} label={linkDescription} title="" onClick={() => onFilterBySku(product.productSku)} />
                                    </WithTooltip>
                                )}
                            </div>
                            {product.productSku && (
                                <span className="body-xs text-secondary">
                                    {product.productSku}
                                    {product.productType !== undefined && ` • ${t(VossProductTypeText[product.productType])}`}
                                </span>
                            )}
                        </div>
                    </div>
                </td>
                <td className="text-right"></td>
                <td className="text-center">
                    <button type="button" className="btn btn-tertiary btn-sm" onClick={onAdjustAllPlans} disabled={product.plans.every((plan) => plan.status !== 'Draft')}>
                        <SpirisIcon name="todo" size="sm" />
                        {t('priceSimulation.adjustAllPlans')}
                    </button>
                </td>
                <td className="text-right"></td>
            </tr>

            {product.plans.map((plan) => (
                <WorksheetPlanRow key={plan.id} productId={product.id} plan={plan} editor={editor} />
            ))}
        </>
    );
};

export default WorksheetProductRow;
